import { useState } from 'react'

import { Button, Form } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'
import { BudgetFragment, EmployeeForSelectFragment } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useForm } from 'modules/core/hooks/useForm'
import { track } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { isNumber } from 'modules/core/lib/validateNumberInput'
import app from 'modules/core/store/models/app'

import { BudgetExceededOptionResultType } from 'modules/finance/components/Budgets/BudgetExceededOptionSelector'
import { useCreateBudgetAllocation } from 'modules/finance/hooks/useBudgetAllocations'
import { allocationsOverBudget } from 'modules/finance/lib/budget_allocations'
import { events } from 'modules/finance/lib/events'

import AddEmployeeModalForm from './Form'

interface AddEmployeeModalProps {
  closeModal: () => void
  employeeIdsWithBudget: number[]
  budget: BudgetFragment
  refetchBudget: (param: { id: number; periodStart: string }) => void
}

interface FormValues {
  limitPerEmployee?: string
  newBudgetLimit?: number
  selectedEmployeeIds: number[]
}

const AddEmployeeModal = ({
  closeModal,
  employeeIdsWithBudget,
  budget,
  refetchBudget,
}: AddEmployeeModalProps) => {
  const [newSelectedEmployees, setNewSelectedEmployees] = useState<EmployeeForSelectFragment[]>([])
  const [displayExtraSection, setDisplayExtraSection] = useState(false)
  const [selectorResult, setSelectorResult] = useState<BudgetExceededOptionResultType>({
    increaseLimit: 0,
    tryAnotherAllocation: 0,
    type: '',
  })
  const { createBudgetAllocation } = useCreateBudgetAllocation({
    budgetId: Number(budget.id),
  })

  const handleModalSubmit = () => {
    const valid = validateData(form)

    if (!valid) {
      return
    }
    form.handleSubmit()
  }

  const validateData = (form: FormikProps<FormValues>) => {
    let valid = true
    const limitPerEmployee = Number(form.values.limitPerEmployee)

    if (form.values?.limitPerEmployee) {
      if (!isNumber(form.values?.limitPerEmployee) || limitPerEmployee < 0) {
        valid = false
        form.setErrors({
          limitPerEmployee: i18n.t(
            'budgets.detail.allocation_per_employee_modal.validate.no_number'
          ),
        })
      }

      if (limitPerEmployee <= 0) {
        valid = false
        form.setErrors({
          limitPerEmployee: i18n.t(
            'budgets.detail.allocation_per_employee_modal.validate.limit_greater'
          ),
        })
      }

      if (
        allocationsOverBudget(
          budget.amountCents,
          limitPerEmployee * 100,
          form.values.selectedEmployeeIds.length,
          budget.totalAllocatedAmountCents
        ) &&
        !selectorResult.type
      ) {
        valid = false
        form.setErrors({
          limitPerEmployee: i18n.t(
            'budgets.detail.allocation_per_employee_modal.validate.budget_limit_exceeded'
          ),
        })
        setDisplayExtraSection(true)
      }
    }
    return valid
  }

  const form = useForm<FormValues>({
    defaultValues: {
      selectedEmployeeIds: [],
    },
    onSubmit: async (data) => {
      let limit = Number(data.limitPerEmployee) * 100

      if (selectorResult.type === 'tryAnotherAllocation') {
        limit = selectorResult.tryAnotherAllocation
      }

      if (selectorResult.type === 'increaseLimit') {
        const newBudgetLimit = selectorResult.increaseLimit

        return handleAddEmployee(limit, newBudgetLimit)
      } else {
        return handleAddEmployee(limit)
      }
    },
  })

  const handleAddEmployee = async (amountCents: number, newBudgetLimit?: number) => {
    const response = await createBudgetAllocation({
      employeeIds: newSelectedEmployees.map((employee) => employee.id),
      budgetId: Number(budget.id),
      amountCents: amountCents,
      budgetLimitAmountCents: newBudgetLimit,
    })

    const result = handleResult(response, (_) => {})

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('budgets.detail.add_employee_modal.submit_success'),
      })
      track(events.addEmployeesToBudget, { budgetId: budget.id })
      refetchBudget({
        id: budget.id,
        periodStart: budget.currentPeriodStart,
      })
      closeModal()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('budgets.detail.add_employee_modal.submit_error'),
      })
    }
    return result
  }

  const handleEmployeesSelected = (employees: EmployeeForSelectFragment[]) => {
    form.setFieldValue(
      'selectedEmployeeIds',
      employees.map((employee) => employee.id)
    )
    setNewSelectedEmployees(employees)
  }

  return (
    <Modal
      illustration={displayExtraSection ? 'document-edit' : 'add-user'}
      title={
        displayExtraSection
          ? i18n.t('budgets.detail.add_employee_modal.budget_limit_exceeded.title')
          : i18n.t('budgets.detail.add_employee_modal.title')
      }
      description={
        displayExtraSection
          ? i18n.t('budgets.detail.add_employee_modal.budget_limit_exceeded.description')
          : i18n.t('budgets.detail.add_employee_modal.description')
      }
      onSubmit={() => handleModalSubmit()}
      onClose={closeModal}
      footer={
        <>
          <Button type="secondary" onClick={closeModal}>
            {i18n.t('common.close')}
          </Button>
          <Button submit disabled={form.isSubmitting}>
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      <Form padding="s0">
        <AddEmployeeModalForm
          currency={budget.currency}
          budgetId={budget.id}
          budgetLimit={budget.amountCents}
          totalAllocatedAmountCents={budget.totalAllocatedAmountCents}
          form={form}
          displayExtraSection={displayExtraSection}
          setDisplayExtraSection={setDisplayExtraSection}
          setSelectorResult={setSelectorResult}
          employeeIdsWithBudget={employeeIdsWithBudget}
          onEmployeesSelected={handleEmployeesSelected}
        />
      </Form>
    </Modal>
  )
}

export default AddEmployeeModal
